/**
 * Top-level shell — Faz 8.1 (ADR-061).
 *
 * Layout: persistent sidebar with five view links + token settings,
 * main pane is a `<Routes>` switch. Routes are intentionally
 * URL-addressable (correlationId, tickId, range) so investigators
 * can paste links into incident tickets and reach the same view.
 */

import type { ReactElement } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";

import { TokenInput } from "./components/TokenInput.js";
import { EventTimelineView } from "./views/EventTimelineView.js";
import { EventDetailView } from "./views/EventDetailView.js";
import { LineageGraphView } from "./views/LineageGraphView.js";
import { PositionTimelineView } from "./views/PositionTimelineView.js";
import { ReplayPlayerView } from "./views/ReplayPlayerView.js";
import { StatePanelsView } from "./views/StatePanelsView.js";

const NAV: ReadonlyArray<{ to: string; label: string }> = [
  { to: "/events", label: "Event Timeline" },
  { to: "/positions", label: "Position Timeline" },
  { to: "/replay", label: "Replay Player" },
  { to: "/state", label: "State Snapshot" },
];

export const App = (): ReactElement => (
  <div className="flex h-full">
    <aside className="w-60 shrink-0 border-r border-slate-800 bg-slate-900/60 p-4 flex flex-col gap-3">
      <h1 className="text-base font-semibold text-slate-100">Risk Alarm Replay</h1>
      <nav className="flex flex-col gap-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `rounded px-3 py-2 text-sm transition ${
                isActive
                  ? "bg-slate-700 text-slate-50"
                  : "text-slate-300 hover:bg-slate-800"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto">
        <TokenInput />
      </div>
    </aside>
    <main className="flex-1 overflow-auto p-6">
      <Routes>
        <Route path="/" element={<Navigate to="/events" replace />} />
        <Route path="/events" element={<EventTimelineView />} />
        <Route path="/events/:tickId" element={<EventDetailView />} />
        <Route path="/lineage/:correlationId" element={<LineageGraphView />} />
        <Route path="/positions" element={<PositionTimelineView />} />
        <Route path="/positions/:symbol" element={<PositionTimelineView />} />
        <Route path="/replay" element={<ReplayPlayerView />} />
        <Route path="/state" element={<StatePanelsView />} />
        <Route path="/state/:tickId" element={<StatePanelsView />} />
      </Routes>
    </main>
  </div>
);
